import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import api from '../services/api';

/* ============================================================
   ACORDO DE CONFIDENCIALIDADE E RGPD — PÁGINA DO FORNECEDOR
   Link único (token, sem login) enviado pela SPROD. O fornecedor
   lê o acordo, marca as declarações e assina; recebe cópia em PDF.
   Fora do Layout (como o portal de subcontratados).
   ============================================================ */

const fmtDataHora = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return '—'; }
};
const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mesma marcação do backend: "## " título, "- " ponto de lista, linha em branco = parágrafo.
function blocos(texto) {
    const out = [];
    let par = [];
    const fechar = () => { if (par.length) { out.push({ t: 'p', s: par.join(' ') }); par = []; } };
    for (const bruta of String(texto || '').replace(/\r\n?/g, '\n').split('\n')) {
        const l = bruta.trim();
        if (!l) { fechar(); continue; }
        if (l.startsWith('## ')) { fechar(); out.push({ t: 'h', s: l.slice(3).trim() }); continue; }
        if (l.startsWith('- ')) { fechar(); out.push({ t: 'li', s: l.slice(2).trim() }); continue; }
        par.push(l);
    }
    fechar();
    return out;
}

/* ---------- Assinatura a partir de uma imagem ----------
   Fotografia da assinatura em papel (ou PNG já recortado) → o mesmo PNG que a
   caixa de desenho produz: o fundo claro do papel passa a transparente,
   recorta-se à volta do traço e reduz-se (máx. 900×300, < 480 KB — o
   servidor aceita até 500 KB). Assim no PDF fica só a assinatura. */
function prepararAssinatura(file) {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            try { resolve(tratarImagemAssinatura(img)); }
            catch { resolve({ erro: 'Não foi possível tratar esta imagem. Tente outra (PNG ou JPEG).' }); }
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve({ erro: 'Não foi possível abrir esta imagem. Use PNG ou JPEG (no iPhone, partilhe a fotografia como JPEG).' });
        };
        img.src = url;
    });
}

function tratarImagemAssinatura(img) {
    // Cópia de trabalho até 1600 px: resolução que chegue para recortar à volta do traço.
    const k = Math.min(1, 1600 / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * k));
    const h = Math.max(1, Math.round(img.naturalHeight * k));
    const cv = document.createElement('canvas');
    cv.width = w; cv.height = h;
    const ctx = cv.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    const dados = ctx.getImageData(0, 0, w, h);
    const px = dados.data;
    const n = w * h;

    // Tom do papel = luminância do percentil 90 (pixels transparentes contam como papel).
    const lum = new Uint8Array(n);
    const hist = new Uint32Array(256);
    for (let i = 0, j = 0; j < n; i += 4, j++) {
        const l = px[i + 3] < 16 ? 255 : Math.round(0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]);
        lum[j] = l;
        hist[l]++;
    }
    let acc = 0, papel = 255;
    for (let l = 0; l < 256; l++) { acc += hist[l]; if (acc >= 0.9 * n) { papel = l; break; } }

    // Papel → transparente, traço → opaco, com transição suave entre os dois limiares.
    const alto = papel * 0.82, baixo = papel * 0.62;
    let x0 = w, y0 = h, x1 = -1, y1 = -1, tinta = 0;
    for (let i = 0, j = 0; j < n; i += 4, j++) {
        const l = lum[j];
        const a = (px[i + 3] < 16 || l >= alto) ? 0 : (l <= baixo ? 255 : Math.round(255 * (alto - l) / (alto - baixo)));
        px[i + 3] = Math.min(px[i + 3], a);
        if (px[i + 3] > 96) {
            tinta++;
            const x = j % w, y = (j - x) / w;
            x0 = Math.min(x0, x); x1 = Math.max(x1, x);
            y0 = Math.min(y0, y); y1 = Math.max(y1, y);
        }
    }
    const frac = tinta / n;
    if (tinta < 30 || frac < 0.0005) {
        return { erro: 'Não se vê a assinatura na imagem. Use uma fotografia nítida, com a assinatura em papel branco.' };
    }
    if (frac > 0.45) {
        return { erro: 'A imagem não parece uma assinatura em fundo claro. Use uma fotografia da assinatura em papel branco, ou um PNG de fundo transparente.' };
    }
    ctx.putImageData(dados, 0, 0);

    // Recorte à volta do traço, com margem, e redução até caber no limite.
    const m = Math.round(Math.max(x1 - x0, y1 - y0) * 0.04) + 4;
    x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m);
    x1 = Math.min(w - 1, x1 + m); y1 = Math.min(h - 1, y1 + m);
    const cw = x1 - x0 + 1, ch = y1 - y0 + 1;
    let escala = Math.min(1, 900 / cw, 300 / ch);
    for (let t = 0; t < 5; t++) {
        const fw = Math.max(1, Math.round(cw * escala)), fh = Math.max(1, Math.round(ch * escala));
        const out = document.createElement('canvas');
        out.width = fw; out.height = fh;
        out.getContext('2d').drawImage(cv, x0, y0, cw, ch, 0, 0, fw, fh);
        const dataUrl = out.toDataURL('image/png');
        if (dataUrl.length * 0.75 <= 480 * 1024) return { dataUrl };
        escala *= 0.7;
    }
    return { erro: 'A imagem continua demasiado pesada. Recorte a fotografia à volta da assinatura e tente de novo.' };
}

function TextoAcordo({ texto, onFim }) {
    const ref = useRef(null);
    const verificar = useCallback(() => {
        const el = ref.current;
        if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) onFim?.();
    }, [onFim]);
    useEffect(() => { verificar(); }, [verificar, texto]);

    const itens = [];
    let lista = [];
    const fecharLista = (k) => { if (lista.length) { itens.push(<ul key={`ul-${k}`} style={sty.ul}>{lista}</ul>); lista = []; } };
    blocos(texto).forEach((b, i) => {
        if (b.t === 'li') { lista.push(<li key={i} style={sty.li}>{b.s}</li>); return; }
        fecharLista(i);
        itens.push(b.t === 'h'
            ? <h3 key={i} style={sty.h3}>{b.s}</h3>
            : <p key={i} style={sty.p}>{b.s}</p>);
    });
    fecharLista('fim');
    return <div ref={ref} onScroll={verificar} style={sty.texto}>{itens}</div>;
}

const AcordoFornecedorPublico = () => {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [erro, setErro] = useState('');
    const [loading, setLoading] = useState(true);
    const [leuTudo, setLeuTudo] = useState(false);
    const [form, setForm] = useState({ nome: '', cargo: '', email: '' });
    const [aceites, setAceites] = useState({});
    const [erros, setErros] = useState({});
    const [enviando, setEnviando] = useState(false);
    const [msgErro, setMsgErro] = useState('');
    const [assinadoAgora, setAssinadoAgora] = useState(null);
    const [verTexto, setVerTexto] = useState(false);
    const sigRef = useRef(null);
    const [modoAss, setModoAss] = useState('desenho'); // 'desenho' (na caixa) | 'imagem' (carregada)
    const [imgAss, setImgAss] = useState(null);         // PNG (data URL) já tratado
    const [aTratarImg, setATratarImg] = useState(false);
    const [sigLargura, setSigLargura] = useState(() => Math.max(260, Math.min((typeof window !== 'undefined' ? window.innerWidth : 600) - 90, 560)));

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/acordos-fornecedor/public/${token}`, { params: { _t: Date.now() } });
            setData(r.data);
            setForm(f => ({ ...f, email: f.email || r.data?.sugestao_email || '' }));
            setErro('');
        } catch (e) {
            setErro(e.response?.data?.error || 'Não foi possível abrir o acordo. Verifique a ligação à Internet e tente novamente.');
        } finally { setLoading(false); }
    }, [token]);
    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        const aoRedimensionar = () => setSigLargura(Math.max(260, Math.min(window.innerWidth - 90, 560)));
        window.addEventListener('resize', aoRedimensionar);
        return () => window.removeEventListener('resize', aoRedimensionar);
    }, []);

    const marcarFim = useCallback(() => setLeuTudo(true), []);
    const upd = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErros(p => ({ ...p, [k]: undefined })); };

    const escolherImagem = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setATratarImg(true);
        const r = await prepararAssinatura(file);
        setATratarImg(false);
        if (r.erro) { setErros(p => ({ ...p, assinatura: r.erro })); return; }
        setImgAss(r.dataUrl);
        setErros(p => ({ ...p, assinatura: undefined }));
    };

    const assinar = async (e) => {
        e.preventDefault();
        if (enviando) return;
        const novos = {};
        if (form.nome.trim().length < 3) novos.nome = 'Indique o nome completo.';
        if (form.cargo.trim().length < 2) novos.cargo = 'Indique o cargo / função.';
        if (!EMAIL_RE.test(form.email.trim())) novos.email = 'Indique um email válido — é para lá que enviamos a cópia.';
        if ((data?.aceitacoes || []).some(a => !aceites[a.k])) novos.aceites = 'Marque todas as declarações para poder assinar.';
        if (modoAss === 'imagem' && !imgAss) novos.assinatura = 'Carregue a imagem da assinatura (ou mude para «Desenhar»).';
        if (modoAss === 'desenho' && (!sigRef.current || sigRef.current.isEmpty())) novos.assinatura = 'Assine dentro da caixa (com o dedo ou com o rato).';
        setErros(novos);
        if (Object.keys(novos).length) { setMsgErro('Falta preencher alguns campos (assinalados a vermelho).'); return; }
        setEnviando(true); setMsgErro('');
        try {
            const assinatura = modoAss === 'imagem' ? imgAss : sigRef.current.getCanvas().toDataURL('image/png');
            const payload = {
                nome: form.nome.trim(), cargo: form.cargo.trim(), email: form.email.trim(),
                assinatura, assinatura_origem: modoAss,
            };
            (data?.aceitacoes || []).forEach(a => { payload[a.k] = aceites[a.k] === true; });
            const r = await api.post(`/acordos-fornecedor/public/${token}/assinar`, payload, { timeout: 60000 });
            setAssinadoAgora({ em: r.data?.assinado_em, email: r.data?.email || payload.email });
            await carregar();
            window.scrollTo(0, 0);
        } catch (err) {
            setMsgErro(err.response?.data?.error || 'Não foi possível registar a assinatura. Tente novamente.');
            if (err.response?.status === 409 || err.response?.status === 410) carregar();
        } finally { setEnviando(false); }
    };

    const pdfUrl = `${api.defaults.baseURL}/acordos-fornecedor/public/${token}/pdf`;

    if (loading && !data) return <div style={sty.wrap}><div style={sty.card}><p style={{ color: '#64748b', textAlign: 'center' }}>A carregar o acordo…</p></div></div>;
    if (erro) {
        return (
            <div style={sty.wrap}>
                <div style={sty.card}>
                    <div style={sty.brand}>SPROD</div>
                    <div style={{ ...sty.aviso, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b' }}>{erro}</div>
                </div>
            </div>
        );
    }

    const assinado = data?.estado === 'assinado' ? data.assinado : null;

    return (
        <div style={sty.wrap}>
            <div style={sty.card}>
                <div style={sty.brand}>SPROD</div>
                <h1 style={sty.titulo}>{data.titulo}</h1>
                <div style={sty.sub}>
                    <strong>{data.fornecedor?.nome}</strong>{data.fornecedor?.nif ? ` · NIF ${data.fornecedor.nif}` : ''}
                    <span style={{ color: '#94a3b8' }}> · versão {data.versao}</span>
                </div>

                {assinado ? (
                    <>
                        <div style={{ ...sty.aviso, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>
                            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>✅ Acordo assinado</div>
                            Assinado em <strong>{fmtDataHora(assinado.em)}</strong> por <strong>{assinado.nome}</strong>{assinado.cargo ? ` (${assinado.cargo})` : ''}.
                            {assinadoAgora && <div style={{ marginTop: 6 }}>Obrigado! Enviámos uma cópia em PDF para <strong>{assinadoAgora.email}</strong>.</div>}
                        </div>
                        <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={sty.btnPdf}>📄 Descarregar a cópia do acordo (PDF)</a>
                        <button type="button" onClick={() => setVerTexto(v => !v)} style={sty.btnLink}>
                            {verTexto ? '▾ Esconder o texto assinado' : '▸ Ver o texto assinado'}
                        </button>
                        {verTexto && <TextoAcordo texto={data.texto} />}
                    </>
                ) : (
                    <form onSubmit={assinar} noValidate>
                        <p style={{ ...sty.p, marginTop: 4 }}>
                            Leia o acordo abaixo. Para assinar, marque as declarações, identifique-se e assine na caixa
                            (ou carregue uma imagem da sua assinatura). Demora cerca de 2 minutos.
                            {data.validade && <> Este link é válido até <strong>{fmtData(data.validade)}</strong>.</>}
                        </p>

                        <TextoAcordo texto={data.texto} onFim={marcarFim} />
                        <div style={{ fontSize: 13, color: leuTudo ? '#166534' : '#94a3b8', margin: '6px 0 14px' }}>
                            {leuTudo ? '✓ Leu o acordo até ao fim.' : '↓ Desça no texto para ler o acordo até ao fim.'}
                        </div>

                        <div style={sty.secao}>Declarações</div>
                        {(data.aceitacoes || []).map(a => (
                            <label key={a.k} style={{ ...sty.check, borderColor: erros.aceites && !aceites[a.k] ? '#fca5a5' : '#e2e8f0' }}>
                                <input type="checkbox" checked={!!aceites[a.k]} style={sty.checkInput}
                                    onChange={e => { setAceites(p => ({ ...p, [a.k]: e.target.checked })); setErros(p => ({ ...p, aceites: undefined })); }} />
                                <span>{a.t}</span>
                            </label>
                        ))}
                        {erros.aceites && <div style={sty.erroCampo}>{erros.aceites}</div>}

                        <div style={sty.secao}>Quem assina</div>
                        <div style={sty.grelha}>
                            <div>
                                <label style={sty.lbl}>Nome completo *</label>
                                <input value={form.nome} onChange={e => upd('nome', e.target.value)} autoComplete="name" maxLength={120}
                                    style={{ ...sty.inp, borderColor: erros.nome ? '#f87171' : '#cbd5e1' }} />
                                {erros.nome && <div style={sty.erroCampo}>{erros.nome}</div>}
                            </div>
                            <div>
                                <label style={sty.lbl}>Cargo / função *</label>
                                <input value={form.cargo} onChange={e => upd('cargo', e.target.value)} autoComplete="organization-title" maxLength={120}
                                    placeholder="ex.: Gerente" style={{ ...sty.inp, borderColor: erros.cargo ? '#f87171' : '#cbd5e1' }} />
                                {erros.cargo && <div style={sty.erroCampo}>{erros.cargo}</div>}
                            </div>
                        </div>
                        <div style={{ marginTop: 10 }}>
                            <label style={sty.lbl}>Email (recebe a cópia em PDF) *</label>
                            <input type="email" value={form.email} onChange={e => upd('email', e.target.value)} autoComplete="email" maxLength={200}
                                style={{ ...sty.inp, borderColor: erros.email ? '#f87171' : '#cbd5e1' }} />
                            {erros.email && <div style={sty.erroCampo}>{erros.email}</div>}
                        </div>

                        <div style={sty.secao}>Assinatura *</div>
                        <div style={sty.modos} role="tablist">
                            {[['desenho', '✍️ Desenhar'], ['imagem', '🖼️ Carregar imagem']].map(([m, rotulo]) => (
                                <button key={m} type="button" role="tab" aria-selected={modoAss === m}
                                    onClick={() => { setModoAss(m); setErros(p => ({ ...p, assinatura: undefined })); }}
                                    style={modoBtn(modoAss === m)}>{rotulo}</button>
                            ))}
                        </div>

                        {/* As duas ficam montadas: trocar de modo não apaga o que já estava feito. */}
                        <div style={{ display: modoAss === 'desenho' ? 'block' : 'none' }}>
                            <div style={{ ...sty.sigBox, borderColor: erros.assinatura ? '#f87171' : '#94a3b8' }}>
                                <SignatureCanvas ref={sigRef} penColor="#0f172a"
                                    onBegin={() => setErros(p => ({ ...p, assinatura: undefined }))}
                                    canvasProps={{ width: sigLargura, height: 150, style: { display: 'block', touchAction: 'none' } }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                                <span style={{ fontSize: 12, color: '#94a3b8' }}>Assine com o dedo (telemóvel) ou com o rato.</span>
                                <button type="button" onClick={() => sigRef.current?.clear()} style={sty.btnSec}>Limpar</button>
                            </div>
                        </div>

                        <div style={{ display: modoAss === 'imagem' ? 'block' : 'none' }}>
                            {imgAss ? (
                                <>
                                    <div style={{ ...sty.sigBox, ...sty.previewAss, borderColor: erros.assinatura ? '#f87171' : '#94a3b8' }}>
                                        <img src={imgAss} alt="Assinatura carregada" style={{ maxWidth: '100%', maxHeight: 150, display: 'block' }} />
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 12, color: '#94a3b8' }}>É assim que a assinatura fica no acordo.</span>
                                        <span style={{ display: 'flex', gap: 6 }}>
                                            <label style={sty.btnSec}>
                                                Trocar
                                                <input type="file" accept="image/*" onChange={escolherImagem} style={{ display: 'none' }} />
                                            </label>
                                            <button type="button" onClick={() => setImgAss(null)} style={sty.btnSec}><DeleteIcon /> Remover</button>
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <label style={{ ...sty.upload, borderColor: erros.assinatura ? '#f87171' : '#94a3b8', opacity: aTratarImg ? 0.6 : 1 }}>
                                    <input type="file" accept="image/*" onChange={escolherImagem} disabled={aTratarImg} style={{ display: 'none' }} />
                                    <span style={{ fontSize: 28 }}>🖼️</span>
                                    <strong style={{ color: '#1e40af' }}>{aTratarImg ? 'A preparar a imagem…' : 'Escolher a imagem da assinatura'}</strong>
                                    <span style={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
                                        Fotografia da assinatura em papel branco, ou PNG de fundo transparente.
                                        O fundo é retirado automaticamente.
                                    </span>
                                </label>
                            )}
                        </div>
                        {erros.assinatura && <div style={sty.erroCampo}>{erros.assinatura}</div>}

                        {msgErro && <div style={{ ...sty.aviso, background: '#fef2f2', borderColor: '#fecaca', color: '#991b1b', marginTop: 14 }}>{msgErro}</div>}

                        <button type="submit" disabled={enviando} style={{ ...sty.btnPri, opacity: enviando ? 0.6 : 1 }}>
                            {enviando ? 'A registar a assinatura…' : '✍️ Assinar o acordo'}
                        </button>
                        <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 10, lineHeight: 1.45 }}>
                            Ao assinar, ficam registados o nome, o cargo e o email de quem assina, a data e hora e o endereço IP de origem.
                            A SPROD trata estes dados apenas para comprovar a aceitação deste acordo.
                        </p>
                    </form>
                )}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 14, textAlign: 'center' }}>
                {data.empresa?.nome || 'SPROD'} · Sistema de Gestão
            </div>
        </div>
    );
};

const sty = {
    wrap: { minHeight: '100vh', background: '#f1f5f9', padding: '24px 12px', fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif', boxSizing: 'border-box' },
    card: { background: '#fff', borderRadius: 16, boxShadow: '0 10px 40px rgba(15,23,42,0.08)', padding: '22px 20px', maxWidth: 760, margin: '0 auto', boxSizing: 'border-box' },
    brand: { fontWeight: 800, letterSpacing: '0.18em', color: '#1e40af', fontSize: 14, marginBottom: 10 },
    titulo: { margin: '0 0 4px', fontSize: 22, lineHeight: 1.25, color: '#0f172a' },
    sub: { color: '#475569', fontSize: 15, marginBottom: 12 },
    texto: { border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', maxHeight: '55vh', overflowY: 'auto', background: '#f8fafc', WebkitOverflowScrolling: 'touch' },
    h3: { fontSize: 15.5, margin: '14px 0 6px', color: '#0f172a' },
    p: { fontSize: 15, lineHeight: 1.55, color: '#334155', margin: '0 0 10px' },
    ul: { margin: '0 0 10px', paddingLeft: 22 },
    li: { fontSize: 15, lineHeight: 1.5, color: '#334155', marginBottom: 4 },
    secao: { fontSize: 13, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '18px 0 8px', borderBottom: '1px solid #e2e8f0', paddingBottom: 4 },
    check: { display: 'flex', gap: 10, alignItems: 'flex-start', border: '1px solid #e2e8f0', borderRadius: 10, padding: '11px 12px', marginBottom: 8, fontSize: 15, lineHeight: 1.45, color: '#1e293b', cursor: 'pointer' },
    checkInput: { width: 22, height: 22, marginTop: 1, flex: '0 0 auto', cursor: 'pointer' },
    grelha: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 },
    lbl: { display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4 },
    inp: { width: '100%', boxSizing: 'border-box', padding: '11px 12px', border: '1px solid #cbd5e1', borderRadius: 10, fontSize: 16 },
    erroCampo: { color: '#b91c1c', fontSize: 13, marginTop: 4 },
    sigBox: { border: '2px dashed #94a3b8', borderRadius: 10, background: '#fff', width: 'fit-content', maxWidth: '100%', overflow: 'hidden' },
    modos: { display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 10, overflow: 'hidden', marginBottom: 10 },
    previewAss: { width: '100%', boxSizing: 'border-box', padding: 12, display: 'flex', justifyContent: 'center', minHeight: 110, alignItems: 'center' },
    upload: {
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center', cursor: 'pointer',
        border: '2px dashed #94a3b8', borderRadius: 10, background: '#f8fafc', padding: '22px 16px', boxSizing: 'border-box',
    },
    aviso: { border: '1px solid', borderRadius: 12, padding: '12px 14px', fontSize: 15, lineHeight: 1.5, margin: '10px 0 14px' },
    btnPri: { display: 'block', width: '100%', marginTop: 18, padding: '15px', border: 'none', borderRadius: 12, background: '#2563eb', color: '#fff', fontSize: 17, fontWeight: 700, cursor: 'pointer' },
    btnSec: { padding: '9px 16px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 10, fontSize: 14, cursor: 'pointer', color: '#334155' },
    btnPdf: { display: 'block', textAlign: 'center', padding: '14px', borderRadius: 12, background: '#2563eb', color: '#fff', textDecoration: 'none', fontWeight: 700, fontSize: 16, marginBottom: 12 },
    btnLink: { border: 'none', background: 'transparent', color: '#2563eb', fontSize: 15, cursor: 'pointer', padding: '6px 0', marginBottom: 8 },
};

const modoBtn = (ativo) => ({
    padding: '10px 16px', border: 'none', fontSize: 15, cursor: 'pointer',
    background: ativo ? '#2563eb' : '#fff', color: ativo ? '#fff' : '#334155', fontWeight: ativo ? 700 : 500,
});

export default AcordoFornecedorPublico;
